/**
 * Trader pricelists (website roadmap S33 — weav3r's Pricelists, the Trading Companion's pricelist use, trader discovery;
 * terms version PRICELIST_TERMS = 16). A trader (a signed-in Omni-Torn user) keeps ONE list of what they pay: per item a
 * buy price — a fixed amount, or a percentage of the item's market value that follows it — with an optional note, and the
 * list as a whole open (buying now) or closed. Omni-Torn users (invite-only) read it on the website (`/pricelist/{id}`,
 * the trader directory) and in the extension's trade helper ("their pricelist pays $X" on a trade with that trader).
 *
 * Storing a list is the trader's own content shown to other Omni-Torn users: new in terms version 16; the server keeps a
 * list only from a trader who agreed to it. "Delete my data" removes it. No trade is ever automated from a list.
 *
 * Pure and browser-safe: readers, checks, prices now, the directory's order and a trade's worth at a list's prices.
 * Tested in packages/shared/test/pricelists.test.ts.
 */

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^\s*-?[\d,]+(\.\d+)?\s*$/.test(x) ? Number(x.replace(/[,\s]/g, '')) : null);

/** The terms version that names pricelists: below it the server keeps no list from that trader. */
export const PRICELIST_TERMS = 16;
/** Items one list may hold. */
export const PRICELIST_MAX_ITEMS = 500;
/** An item's note, and the list's own note ("about"), in characters. */
export const PRICELIST_NOTE_MAX = 140;
export const PRICELIST_ABOUT_MAX = 500;
/** A percentage of market value: 1–200 % (one decimal). */
export const PRICELIST_PCT_MIN = 1;
export const PRICELIST_PCT_MAX = 200;
/** A fixed price: $1 up to $100 billion. */
export const PRICELIST_PRICE_MAX = 100_000_000_000;
/** Saves one trader may make a minute (the server's limit). */
export const PRICELIST_SAVES_PER_MINUTE = 20;
/** The request body a save may be (bytes). */
export const PRICELIST_BODY_MAX = 120_000;
/** Rows the trader directory answers at most. */
export const PRICELIST_DIRECTORY_MAX = 200;

export type PriceMode = 'fixed' | 'pct';

/** One item on a list, as the trader set it. */
export interface PricelistEntry {
  itemId: number;
  mode: PriceMode;
  /** fixed: dollars for one; pct: percent of the item's market value. */
  value: number;
  /** The trader's note for this item ('' none). */
  note: string;
}

/** A list as the trader saves it. */
export interface PricelistIn {
  /** Buying now (shown in the directory and the trade helper), or closed. */
  open: boolean;
  /** The trader's note for the whole list ('' none): how to trade with them, hours… */
  about: string;
  items: PricelistEntry[];
}

/** One item with its price now. */
export interface PricedEntry extends PricelistEntry {
  itemName: string | null;
  /** The value a percentage follows: Torn's market value, else the cheapest listing Omni-Torn's pool saw lately. */
  marketValue: number | null;
  marketFrom: 'torn' | 'pool' | null;
  /** What the trader pays for one now (null: a percentage with no market value known). */
  price: number | null;
  /** That price against the market value, percent (one decimal). */
  pctOfMarket: number | null;
  /** When the trader last changed this item (ms). */
  updatedAt: number;
}

/** A trader's list as Omni-Torn users see it (GET /api/pricelists/:id). */
export interface PricelistView {
  traderId: number;
  traderName: string | null;
  open: boolean;
  about: string;
  items: PricedEntry[];
  /** When the trader last saved the list (ms). */
  updatedAt: number;
  /** When the market values used were read (ms; null: none could be read). */
  pricedAt: number | null;
  termsNeeded: number;
}

/** GET /api/pricelists/mine: your own list (null: none yet) and whether the server may keep one for you. */
export interface PricelistMine {
  list: PricelistView | null;
  /** Your agreed terms version, and the one storing a list needs. */
  terms: number;
  termsNeeded: number;
  mayStore: boolean;
}

/** PUT /api/pricelists/mine's answer. */
export interface PricelistSaveAnswer {
  ok: boolean;
  message?: string;
  /** Why it was refused: the terms, the list itself, or too many saves this minute. */
  why?: 'terms' | 'list' | 'busy';
  list?: PricelistView | null;
}

export type DirectorySort = 'items' | 'updated';

/** One trader in the directory (only open lists). */
export interface DirectoryRow {
  traderId: number;
  traderName: string | null;
  items: number;
  updatedAt: number;
  about: string;
}

export interface DirectoryAnswer {
  rows: DirectoryRow[];
  /** How many open lists there are in all. */
  total: number;
  sort: DirectorySort;
  at: number;
  termsNeeded: number;
}

/** The website's page for a trader's list. */
export const pricelistPath = (traderId: number): string => `/pricelist/${traderId}`;

/** A note as kept: no control characters, spaces folded, trimmed, cut to `max`. */
export function cleanNote(x: unknown, max = PRICELIST_NOTE_MAX): string {
  if (typeof x !== 'string') return '';
  return x
    .replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2066-\u2069]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

/** One entry from anything (null: not a usable entry). */
export function cleanEntry(x: unknown): PricelistEntry | null {
  const o = obj(x);
  if (!o) return null;
  const itemId = num(o.itemId);
  if (itemId === null || !Number.isInteger(itemId) || itemId <= 0 || itemId > 1_000_000) return null;
  const mode: PriceMode | null = o.mode === 'pct' ? 'pct' : o.mode === 'fixed' ? 'fixed' : null;
  const v = num(o.value);
  if (!mode || v === null) return null;
  if (mode === 'pct') {
    const pct = Math.round(v * 10) / 10;
    if (pct < PRICELIST_PCT_MIN || pct > PRICELIST_PCT_MAX) return null;
    return { itemId, mode, value: pct, note: cleanNote(o.note) };
  }
  const price = Math.round(v);
  if (price < 1 || price > PRICELIST_PRICE_MAX) return null;
  return { itemId, mode, value: price, note: cleanNote(o.note) };
}

/**
 * A list as a trader sent it → what may be kept, or why not. An item listed twice keeps its last entry; a list longer than
 * PRICELIST_MAX_ITEMS (after that) or any unusable entry is refused whole (nothing half-saved).
 */
export function cleanPricelist(raw: unknown): { ok: true; list: PricelistIn } | { ok: false; message: string } {
  const o = obj(raw);
  if (!o || !Array.isArray(o.items)) return { ok: false, message: 'Expected { open, about, items: [{ itemId, mode: "fixed" | "pct", value, note }] }.' };
  if (o.items.length > PRICELIST_MAX_ITEMS * 2) return { ok: false, message: `A list holds at most ${PRICELIST_MAX_ITEMS} items.` };
  const byId = new Map<number, PricelistEntry>();
  for (let i = 0; i < o.items.length; i++) {
    const e = cleanEntry(o.items[i]);
    if (!e) return { ok: false, message: `Item ${i + 1} isn’t usable: an item id, and a price of $1 or more or ${PRICELIST_PCT_MIN}–${PRICELIST_PCT_MAX}% of market value.` };
    byId.delete(e.itemId);
    byId.set(e.itemId, e);
  }
  if (byId.size > PRICELIST_MAX_ITEMS) return { ok: false, message: `A list holds at most ${PRICELIST_MAX_ITEMS} items.` };
  return { ok: true, list: { open: o.open !== false, about: cleanNote(o.about, PRICELIST_ABOUT_MAX), items: [...byId.values()] } };
}

/** What the trader pays for one now: a fixed price as set; a percentage of the market value (null without one). */
export function priceFor(e: Pick<PricelistEntry, 'mode' | 'value'>, marketValue: number | null): number | null {
  if (e.mode === 'fixed') return e.value;
  if (!marketValue || marketValue <= 0) return null;
  return Math.max(1, Math.round((marketValue * e.value) / 100));
}

/** A price against the market value, percent with one decimal (null without both). */
export function pctOfMarket(price: number | null, marketValue: number | null): number | null {
  if (price === null || !marketValue || marketValue <= 0) return null;
  return Math.round((price / marketValue) * 1000) / 10;
}

/** An entry with its price now. */
export function pricedEntry(e: PricelistEntry, info: { name: string | null; marketValue: number | null; from: 'torn' | 'pool' | null }, updatedAt: number): PricedEntry {
  const price = priceFor(e, info.marketValue);
  return { ...e, itemName: info.name, marketValue: info.marketValue, marketFrom: info.marketValue ? info.from : null, price, pctOfMarket: pctOfMarket(price, info.marketValue), updatedAt };
}

/** The directory's order: most items (then most recently updated), or most recently updated (then most items). */
export function sortDirectory(rows: readonly DirectoryRow[], sort: DirectorySort): DirectoryRow[] {
  return [...rows].sort((a, b) => (sort === 'items' ? b.items - a.items || b.updatedAt - a.updatedAt : b.updatedAt - a.updatedAt || b.items - a.items) || a.traderId - b.traderId);
}

export const directorySortOf = (x: string | null | undefined): DirectorySort => (x === 'updated' ? 'updated' : 'items');

/** One line of a trade at a list's prices. */
export interface ListedLine {
  name: string;
  itemId: number | null;
  qty: number;
  /** The list's price for one (null: not on the list). */
  each: number | null;
  total: number | null;
}

/** What a list pays for items offered (the trade helper: what you give, at the other player's list). */
export function listWorth(list: Pick<PricelistView, 'items'> | null, lines: ReadonlyArray<{ name: string; itemId: number | null; qty: number }>): { lines: ListedLine[]; total: number; listed: number; unlisted: number } {
  const byId = new Map((list?.items ?? []).map((e) => [e.itemId, e.price]));
  let total = 0;
  let listed = 0;
  let unlisted = 0;
  const out = lines.map((l) => {
    const each = l.itemId !== null ? (byId.get(l.itemId) ?? null) : null;
    const t = each === null ? null : each * l.qty;
    if (t === null) unlisted++;
    else {
      listed++;
      total += t;
    }
    return { name: l.name, itemId: l.itemId, qty: l.qty, each, total: t };
  });
  return { lines: out, total, listed, unlisted };
}

/** A trader's list from a server answer (the extension checks what it got). Null: not a list. */
export function readPricelistView(x: unknown): PricelistView | null {
  const o = obj(x);
  const id = num(o?.traderId);
  if (!o || id === null || !Number.isInteger(id) || id <= 0 || !Array.isArray(o.items)) return null;
  const items: PricedEntry[] = [];
  for (const raw of o.items.slice(0, PRICELIST_MAX_ITEMS)) {
    const e = cleanEntry(raw);
    const r = obj(raw)!;
    if (!e) continue;
    const price = num(r.price);
    const mv = num(r.marketValue);
    items.push({ ...e, itemName: typeof r.itemName === 'string' ? r.itemName.slice(0, 80) : null, marketValue: mv, marketFrom: r.marketFrom === 'torn' || r.marketFrom === 'pool' ? r.marketFrom : null, price: price !== null && price > 0 ? Math.round(price) : null, pctOfMarket: num(r.pctOfMarket), updatedAt: num(r.updatedAt) ?? 0 });
  }
  return { traderId: id, traderName: typeof o.traderName === 'string' ? o.traderName.slice(0, 40) : null, open: o.open !== false, about: cleanNote(o.about, PRICELIST_ABOUT_MAX), items, updatedAt: num(o.updatedAt) ?? 0, pricedAt: num(o.pricedAt), termsNeeded: num(o.termsNeeded) ?? PRICELIST_TERMS };
}
